import React, { FC } from 'react';
import styled, { css } from 'styled-components';

interface SpacerProps {
  spacing?: 'half' | 'quarter' | 'double';
  responsive?: boolean;
  direction?: 'vertical' | 'horizontal' | 'reverseVertical';
  as?: any; // It's generally better to use more specific types if possible, e.g., keyof JSX.IntrinsicElements | React.ComponentType<any>
  centerVertical?: boolean;
}

interface StyledSpacerProps {
  $spacing?: SpacerProps['spacing'];
  $responsive?: SpacerProps['responsive'];
  $direction?: SpacerProps['direction'];
  $centerVertical?: SpacerProps['centerVertical'];
  as?: any;
}

const SpacerEl = styled.div<StyledSpacerProps>`
  ${({ theme: { space, media }, $spacing, $responsive, $direction, $centerVertical }) => {
    // Vertical spacing values (derived from the mapping)
    const [childSpacing, childSpacingDesktop] = {
      'quarter': [space.xs, space.md],
      'half': [space.sm, space.lg],
      'default': [space.lg, space.xl], // 'default' is used when 'spacing' prop is undefined for vertical
      'double': [space.xl, space.xxl],
    }[$spacing ?? 'default'];

    // Corrected horizontal spacing logic:
    // Determine the margin value for horizontal spacing based on the 'spacing' prop,
    // aligning with the theme tokens used for vertical spacing but with its specific fallback.
    let horizontalMarginValue;
    if ($spacing === 'quarter') {
      horizontalMarginValue = space.xs; // Corresponds to the base of 'quarter' mapping
    } else if ($spacing === 'half') {
      horizontalMarginValue = space.sm;  // Corresponds to the base of 'half' mapping
    } else if ($spacing === 'double') {
      horizontalMarginValue = space.xl;  // Corresponds to the base of 'double' mapping
    } else {
      // If spacing is undefined, use the specific 'fluidMedium' fallback for horizontal.
      horizontalMarginValue = space.fluidMedium;
    }

    return css`
      ${$direction === 'horizontal'
        ? css`
            display: flex;
            ${$centerVertical &&
            css`
              align-items: center;
            `}

            /* Apply the consistently derived horizontal margin */
            > * + * {
              margin-left: ${horizontalMarginValue};
            }
          `
        : css`
            /* Vertical spacing */
            > * + *:not(:empty) {
              margin-top: ${childSpacing};
            }
          `}

      // Responsive (adjust spacing for desktop)

      ${$responsive &&
      css`
        @media (min-width: ${media.md}) {
          > * + *:not(:empty) {
            margin-top: ${childSpacingDesktop};
          }
        }
      `}
    `;
  }}
`;

export const Spacer: FC<React.PropsWithChildren<SpacerProps>> = ({ children, spacing, as, responsive, ...rest }) => (
  <SpacerEl $spacing={spacing} as={as} $responsive={responsive} {...rest}>
    {children}
  </SpacerEl>
);

export default Spacer;